import { Checkbox } from '@heidi/design-system/checkbox';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { StatusIndicator } from '@heidi/design-system/status-indicator';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { SessionScope, type SessionStatus } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { useUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { Circle } from 'lucide-react';

import { useQueryAllSessionsStatusCounts } from '../../../../../data/query/useQueryAllSessionsStatusCounts';
import { sessionListScopeAtom } from '../../../../../data/session-list/atoms';
import { useQuerySessionCountBySessionStatus } from '../../../../../data/session-list/useQuerySessionStatusCount';
import { useIsMultiScopeStatusCountsEnabled } from '../../../../../flags';
import { useSessionListHost } from '../../../../session-list/session-list-host-context';
import { useStatusFilter } from './hooks/useStatusFilter';

interface StatusFilterItemProps {
  status: SessionStatus;
}

export const StatusFilterItem = ({ status }: StatusFilterItemProps) => {
  const host = useSessionListHost();
  const { t } = useTranslations();
  const isNewSessionListEnabled = host.shell.useIsNewSessionListEnabled();
  const { Loader } = host.shell;
  const { sessionStatusChipConfig, statusIconVariants, sessionStatusDescription } =
    host.sessionApp;
  const { selectedStatusesFilter, handleStatusChange } = useStatusFilter();
  const scope = useAtomValue(sessionListScopeAtom);
  const isMyScope = scope === SessionScope.me;
  const isAllSessionsScope = scope === SessionScope.all;

  // My Sessions keeps the legacy own-session count. All Sessions uses the
  // whole-scope endpoint. Every other scope — Direct Share today — has no count
  // source, so it shows nothing.
  //
  // The whole-scope gate is composed here rather than inside the hook: the
  // scope is a UI atom, the rollout flag is app platform and the loaded-user
  // check is a workspace query, and the hook now lives in a package that reads
  // none of the three. It still supplies the auth half itself. The `scope ===
  // all` term is load-shedding — it stops the whole-scope endpoint firing on
  // other scopes — and it sits next to the display decision it has to move with.
  const { data: { count: legacyCount } = { count: 0 }, isLoading: isLegacyLoading } =
    useQuerySessionCountBySessionStatus(status, { enabled: isMyScope });
  const { data: isUserLoaded } = useUser((data) => data != null);
  const isMultiScopeStatusCountsEnabled = useIsMultiScopeStatusCountsEnabled();
  const { data: allSessionsCount, isLoading: isAllSessionsLoading } =
    useQueryAllSessionsStatusCounts({
      status,
      enabled:
        Boolean(isUserLoaded) && isAllSessionsScope && isMultiScopeStatusCountsEnabled,
    });

  const resolveScopedCount = () => {
    if (isMyScope) return { sessionCount: legacyCount, isLoading: isLegacyLoading };
    if (isAllSessionsScope)
      return { sessionCount: allSessionsCount ?? 0, isLoading: isAllSessionsLoading };
    return { sessionCount: 0, isLoading: false };
  };
  const { sessionCount, isLoading } = resolveScopedCount();

  return (
    <DropdownMenuItem
      className="flex items-center gap-2"
      onSelect={(e) => e.preventDefault()}
      onClick={() => handleStatusChange(status)}
    >
      <Checkbox checked={selectedStatusesFilter.includes(status)} />
      <div className="flex w-full items-center justify-between gap-10">
        <div className="flex items-center gap-2">
          {isNewSessionListEnabled ? (
            <StatusIndicator
              status={sessionStatusChipConfig[status].indicator}
              size="xs"
            />
          ) : (
            <Circle
              size={8}
              fill="currentColor"
              className={cn(
                statusIconVariants({
                  variant: status.toLowerCase() as
                    | 'draft'
                    | 'for_action'
                    | 'reviewed'
                    | 'approved'
                    | 'sent'
                    | 'empty',
                })
              )}
            />
          )}
          <TypographyP2>
            {t(`sessionStatus.${sessionStatusDescription[status]}` as TranslationKey)}
          </TypographyP2>
        </div>
        {isLoading ? (
          <Loader size={14} />
        ) : (
          sessionCount > 0 && (
            <TypographyCaption className="text-text-secondary">
              {sessionCount > 1
                ? t('common.results', { count: sessionCount })
                : t('common.result', { count: sessionCount })}
            </TypographyCaption>
          )
        )}
      </div>
    </DropdownMenuItem>
  );
};
